/**
 * Shared Dashboard API client.
 *
 * Uses a plain axios instance (no Bearer token) for public endpoints.
 * Authenticated link-management endpoints use the standard AuthenticatedClient.
 *
 * Convention: unlike the raw-response repositories, the async API functions
 * here UNWRAP the response and return the payload directly (`response.data`),
 * or `void` for delete/track calls. The session/identity helpers are local
 * (localStorage / module state) and return plain values.
 */

import { getApiBaseUrl } from '@/app/runtime';
import i18n from '@/i18n';
import AuthenticatedClient from '@/repositories/clients/AuthenticatedClient';
import normalizeBaseDomain from '@/repositories/clients/normalizeBaseDomain';
import axios from 'axios';

const baseDomain = normalizeBaseDomain(getApiBaseUrl());

// Public client — no automatic Bearer token
const publicClient = axios.create({ baseURL: baseDomain });
publicClient.interceptors.request.use((config) => {
  config.headers ||= {};
  config.headers['Accept-Language'] = i18n.global.locale.value;
  return config;
});

const SESSION_KEY_PREFIX = 'profiteia:shared:';
const VISITOR_IDENTITY_KEY = 'profiteia:shared:visitor';

// ─── Active share session (module-level, used by API clients) ────────

let _activeShareSession = null;

/**
 * Set the active share session so API clients include X-Share-Session.
 * Call when entering the shared dashboard view after verification.
 */
export function setActiveShareSession(token, sessionToken) {
  _activeShareSession = { token, sessionToken };
}

/**
 * Clear the active share session.
 * Call when leaving the shared dashboard page or when session expires.
 */
export function clearActiveShareSession() {
  _activeShareSession = null;
}

/**
 * Returns the current active share session token, if any.
 * Used by API client interceptors to inject X-Share-Session header.
 */
export function getActiveShareSessionToken() {
  return _activeShareSession?.sessionToken || null;
}

// ─── Session persistence (localStorage) ─────────────────────────────

function _sessionKey(token) {
  return SESSION_KEY_PREFIX + token.substring(0, 12);
}

/** Read a stored (non-expired) session from localStorage. @param {string} token @returns {object|null} session or null */
export function getStoredSession(token) {
  try {
    const raw = localStorage.getItem(_sessionKey(token));
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (new Date(session.expiresAt) < new Date()) {
      localStorage.removeItem(_sessionKey(token));
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

/** Persist a session to localStorage. @param {string} token @param {object} data @returns {void} */
export function storeSession(token, data) {
  try {
    localStorage.setItem(
      _sessionKey(token),
      JSON.stringify({
        sessionToken: data.sessionToken,
        expiresAt: data.expiresAt,
        dashboardTitle: data.dashboardTitle,
      })
    );
  } catch {
    // localStorage unavailable
  }
}

/** Remove a stored session. @param {string} token @returns {void} */
export function clearSession(token) {
  try {
    localStorage.removeItem(_sessionKey(token));
  } catch {
    // ignore
  }
}

// ─── Global visitor identity (shared across all links) ──────────────

/** Read the stored visitor identity. @returns {object|null} */
export function getStoredVisitorIdentity() {
  try {
    const raw = localStorage.getItem(VISITOR_IDENTITY_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Persist the visitor identity. @param {{ fullName: string, email: string, company?: string }} identity @returns {void} */
export function storeVisitorIdentity({ fullName, email, company }) {
  try {
    localStorage.setItem(
      VISITOR_IDENTITY_KEY,
      JSON.stringify({ fullName, email, company: company || '' })
    );
  } catch {
    // localStorage unavailable
  }
}

// ─── Authenticated: link management ─────────────────────────────────

/** Create a share link. Unwrapped. @param {string} dashboardId @param {object} [payload={}] @returns {Promise<object>} created link */
export async function createShareLink(dashboardId, payload = {}) {
  const { data } = await AuthenticatedClient.post(
    `/dashboards/${dashboardId}/share-links`,
    payload
  );
  return data;
}

/** List a dashboard's share links. Unwrapped. @param {string} dashboardId @returns {Promise<object>} links payload */
export async function listShareLinks(dashboardId) {
  const { data } = await AuthenticatedClient.get(`/dashboards/${dashboardId}/share-links`);
  return data;
}

/** Revoke (soft-disable) a share link. @param {string} dashboardId @param {string} linkId @returns {Promise<void>} */
export async function revokeShareLink(dashboardId, linkId) {
  await AuthenticatedClient.delete(`/dashboards/${dashboardId}/share-links/${linkId}`);
}

/** Permanently delete a share link. @param {string} dashboardId @param {string} linkId @returns {Promise<void>} */
export async function deleteShareLink(dashboardId, linkId) {
  await AuthenticatedClient.delete(`/dashboards/${dashboardId}/share-links/${linkId}/permanent`);
}

// ─── Public: visitor flow ────────────────────────────────────────────

/** Validate a public share token. Unwrapped. @param {string} token @returns {Promise<object>} link/dashboard metadata */
export async function validateShareLink(token) {
  const { data } = await publicClient.get(`/shared-dashboards/${encodeURIComponent(token)}`);
  return data;
}

/** Submit visitor identity to begin verification. Unwrapped. @param {string} token @param {{ fullName: string, email: string, company?: string }} identity @returns {Promise<object>} */
export async function submitIdentity(token, { fullName, email, company }) {
  const { data } = await publicClient.post(
    `/shared-dashboards/${encodeURIComponent(token)}/verify`,
    { full_name: fullName, email, company }
  );
  return data;
}

/** Confirm verification code. Returns a camelCased session summary. @param {string} token @param {{ email: string, code: string }} args @returns {Promise<{ sessionToken: string, expiresAt: string, dashboardTitle: string }>} */
export async function confirmVerification(token, { email, code }) {
  const { data } = await publicClient.post(
    `/shared-dashboards/${encodeURIComponent(token)}/verify/confirm`,
    { email, code }
  );
  return {
    sessionToken: data.session_token,
    expiresAt: data.expires_at,
    dashboardTitle: data.dashboard_title,
  };
}

/** Fetch the shared dashboard payload. Unwrapped. @param {string} token @param {string} sessionToken @returns {Promise<object>} dashboard */
export async function fetchSharedDashboard(token, sessionToken) {
  const { data } = await publicClient.get(
    `/shared-dashboards/${encodeURIComponent(token)}/dashboard`,
    { headers: { 'X-Share-Session': sessionToken } }
  );
  return data;
}

/** Fire-and-forget analytics event (errors swallowed). @param {string} token @param {string} sessionToken @param {string} eventType @param {object} [metadata={}] @returns {Promise<void>} */
export async function trackSharedEvent(token, sessionToken, eventType, metadata = {}) {
  try {
    await publicClient.post(
      `/shared-dashboards/${encodeURIComponent(token)}/events`,
      { event_type: eventType, metadata },
      { headers: { 'X-Share-Session': sessionToken } }
    );
  } catch {
    // Non-critical — don't throw
  }
}
